import React, { useState, useEffect } from 'react'
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  CircularProgress,
  Paper,
  Grid,
  Container,
} from '@mui/material'
import { Assessment, Edit } from '@mui/icons-material'
import ReactPaginate from 'react-paginate'
import AdvancedReportConfigModal from './AdvancedReportConfigModal'
import { getAdvancedReportConfigurations } from '../services/AdminService'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import '../Grid.css'
import { IAdvancedSearchConfigSearchRequest } from 'src/viewModels/admin/AdvancedSearchConfigSearchRequest'
import { IAdvancedSearchReportSummary } from 'src/viewModels/admin/AdvancedSearchReportSummary'
import { pagedList } from 'src/viewModels/pagedList'
import { Helmet } from 'react-helmet'
import stores from 'src/Stores'

const AdvancedReportConfiguration = () => {
  const [configurations, setConfigurations] = useState<IAdvancedSearchReportSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [totalItems, setTotalItems] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedConfigurationId, setSelectedConfigurationId] = useState<string | null>(
    null
  )

  const loadConfigurations = async () => {
    setIsLoading(true)

    const request: IAdvancedSearchConfigSearchRequest = {
      page,
      pageSize,
    }
    request.organizationId = stores.global.currentUserOrganization.id!

    getAdvancedReportConfigurations(request)
      .then((response: pagedList) => {
        setTotalItems(response.totalItems!)
        setPageCount(response.totalPages!)
        setConfigurations(response.items)

        if (response.totalItems! > 0) {
          setPaginationOffsetData(response.totalItems!)
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false)
      })
  }

  const setPaginationOffsetData = (totalItems: number) => {
    const firstIndex = (page - 1) * pageSize + 1
    const lastIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    setFirstRecordIndex(firstIndex)
    setLastRecordIndex(lastIndex)
  }

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected + 1
    setPage(newPage)
  }

  useEffect(() => {
    loadConfigurations()
  }, [page])

  const isSGridDataEmpty = () => {
    return !configurations || configurations.length === 0
  }

  const paginationInfo = () => {
    if (!isSGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return <></>
  }

  const openModal = (id: string) => {
    setSelectedConfigurationId(id)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setSelectedConfigurationId(null)
    setIsModalOpen(false)

    loadConfigurations()
  }

  return (
    <>
      <Helmet>
        <title>Advanced Search Configuration</title>
      </Helmet>
      <ResponsiveAppBar title="Advanced Report Configuration" pageIcon={<Assessment />} />

      <Container
        maxWidth="md"
        sx={{ marginTop: 2, marginLeft: 0, marginRight: 'auto', padding: 0 }}
      >
        <Paper sx={{ paddingBottom: 2, marginTop: 2, marginLeft: 2 }}>
          {isLoading ? (
            <div
              style={{
                position: 'fixed',
                display: 'flex',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: '2',
              }}
            >
              <CircularProgress size={100} />
            </div>
          ) : (
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignContent="center"
              alignItems="flex-start"
              wrap="wrap"
            >
              <Grid item xs={12}>
                <Table
                  sx={{ marginTop: 2, marginRight: 2, width: '100%' }}
                  style={{ tableLayout: 'fixed', width: '100%' }}
                >
                  <TableHead>
                    <TableRow className="gridPadding">
                      <TableCell width="45%">Report Name</TableCell>
                      <TableCell
                        width="35%"
                        style={{ textAlign: 'right', paddingRight: '150px' }}
                      >
                        Number of Groups
                      </TableCell>
                      <TableCell width="20%"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {configurations.map((config) => (
                      <TableRow className="gridPadding" key={config.id}>
                        <TableCell>{config.name}</TableCell>
                        <TableCell style={{ textAlign: 'right', paddingRight: '150px' }}>
                          {config.assignedGroupCount}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          <Tooltip title="Edit Advanced Report Configuration">
                            <Edit
                              color="primary"
                              style={{ fontSize: '15px', cursor: 'pointer' }}
                              onClick={() => openModal(config.id!)}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {configurations.length === 0 && (
                      <TableRow className="gridNoPadding">
                        <TableCell colSpan={3} align="center">
                          No reports found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <div className="pagination-row">
                  {paginationInfo()}
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    pageCount={Math.max(1, pageCount)}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageChange}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    forcePage={page - 1}
                    disableInitialCallback={true}
                    renderOnZeroPageCount={() => null}
                  />
                </div>
              </Grid>
            </Grid>
          )}

          {isModalOpen && selectedConfigurationId && (
            <AdvancedReportConfigModal
              isOpen={isModalOpen}
              id={selectedConfigurationId}
              onClose={closeModal}
            />
          )}
        </Paper>
      </Container>
    </>
  )
}

export default AdvancedReportConfiguration
